import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../core/config/api";
import "./bulk_results.css";
import "./email_details.css";

// One verified address (an Instant Verification row), explained: its status
// and reason, and its domain's mail and security records. The Catch-All
// Resolver is for lists of 2 or more emails, so it isn't offered here.
// There are no status tabs: the page always shows the status the address has.

type Status = "valid" | "invalid" | "risky" | "catch_all" | "do_not_send" | "invalid_domain";

interface EmailDetailsResponse {
    request_id: number;
    email: string;
    status: Status;
    raw_status?: string | null;
    reason: string;
    smtp_response_code?: number | null;
    domain?: string | null;
    domain_health?: "Healthy" | "Not Found" | null;
    catch_all?: "Yes" | "No" | null;
    email_provider?: string | null;
    mail_server?: string | null;
    mail_server_ip?: string | null;
    mx_records?: { host: string; priority: number }[];
    spf_found?: boolean | null;
    spf_record?: string | null;
    spf_policy?: string | null;
    dmarc_found?: boolean | null;
    dmarc_record?: string | null;
    dmarc_policy?: string | null;
    catch_all_resolve?: { submitted: number; resolved: number; status: "in_progress" | "completed" | "failed" } | null;
    resolvable: boolean;
    credits_per_email: number;
    available_credits: number;
}

const STATUS_META: Record<Status, { label: string; icon: string }> = {
    valid: { label: "Valid", icon: "validate.svg" },
    invalid: { label: "Invalid", icon: "invalid.svg" },
    risky: { label: "Risky", icon: "risk.svg" },
    catch_all: { label: "Catch-All", icon: "catch-all.svg" },
    do_not_send: { label: "Do Not Send", icon: "do-not-sent.svg" },
    invalid_domain: { label: "Invalid Domain", icon: "invalid-domain.svg" },
};


const flag = (value: boolean | null | undefined) => (value === true ? "Found" : value === false ? "Not Found" : "—");
const show = (value?: string | number | null) => (value === null || value === undefined || value === "" ? "—" : String(value));

const EmailDetails = () => {
    const { id } = useParams();
    const requestId = Number(id);
    const navigate = useNavigate();

    const [data, setData] = useState<EmailDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(
        async (silent = false) => {
            if (!Number.isFinite(requestId)) {
                setError("This verification could not be found.");
                setLoading(false);
                return;
            }
            if (!silent) setLoading(true);
            try {
                const { data: body } = await apiClient.get(`/verifier-requests/re/${requestId}/email-details`);
                setData(body);
                setError(null);
            } catch (err: any) {
                const detail = err?.response?.data?.detail;
                setError(typeof detail === "string" && detail !== "Not Found" ? detail : "Unable to load the email details. Please try again.");
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [requestId]
    );

    useEffect(() => {
        load();
    }, [load]);

    const meta = data ? STATUS_META[data.status] : null;
    const topMx = data?.mx_records?.[0];

    return (
        <div className="bulk-results-page email-details-page">
            <button type="button" className="bulk-results-back" onClick={() => navigate("/email-verifier?tab=single")}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back
            </button>

            <h1 className="bulk-results-title ed-title">Email Details</h1>

            <section className="ed-card">
                {loading ? (
                    <div className="ed-placeholder">
                        <div className="spinner-border spinner-border-sm" role="status" />
                        <span>Loading email details...</span>
                    </div>
                ) : error || !data || !meta ? (
                    <div className="ed-placeholder">
                        <span>{error || "Unable to load the email details."}</span>
                        <button type="button" className="ed-retry" onClick={() => load()}>Retry</button>
                    </div>
                ) : (
                    <>
                        <div className="ed-status-head">
                            <span className={`ed-status-icon status-${data.status}`}>
                                <img src={`/images/${meta.icon}`} alt="" width={18} height={18} />
                            </span>
                            <span className="ed-status-label">{meta.label}</span>
                        </div>

                        <div className="ed-summary">
                            <span className="ed-email" title={data.email}>{data.email}</span>
                            <span className="ed-divider" aria-hidden="true" />
                            <span className="ed-reason">{data.reason}</span>
                            <span className="ed-pills">
                                <span className={`ed-pill status-${data.status}`}>
                                    Email <strong>{meta.label}</strong>
                                </span>
                                {data.domain_health && (
                                    <span className={`ed-pill ${data.domain_health === "Healthy" ? "domain-ok" : "domain-bad"}`}>
                                        Domain <strong>{data.domain_health}</strong>
                                    </span>
                                )}
                            </span>
                        </div>

                        <div className="ed-panels">
                            <div className="ed-panel">
                                <h2 className="ed-panel-title">
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
                                        <path d="M1.5 8h13M8 1.5c1.8 1.9 2.7 4 2.7 6.5S9.8 12.6 8 14.5C6.2 12.6 5.3 10.5 5.3 8S6.2 3.4 8 1.5Z" stroke="currentColor" strokeWidth="1.2" />
                                    </svg>
                                    Domain Insights
                                </h2>
                                <dl className="ed-grid">
                                    <div><dt>Domain</dt><dd>{show(data.domain)}</dd></div>
                                    <div><dt>Email Provider</dt><dd>{show(data.email_provider)}</dd></div>
                                    <div><dt>Mail Server</dt><dd title={data.mail_server || undefined}>{show(data.mail_server)}</dd></div>
                                    <div><dt>Catch-All</dt><dd>{show(data.catch_all)}</dd></div>
                                </dl>
                            </div>

                            <div className="ed-panel">
                                <h2 className="ed-panel-title">
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="M8 1.5 13.5 3.5v4.2c0 3.2-2.3 5.8-5.5 6.8-3.2-1-5.5-3.6-5.5-6.8V3.5L8 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                                        <path d="m5.8 8 1.6 1.6L10.4 6.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    Email Security
                                </h2>
                                <dl className="ed-grid">
                                    <div><dt>SPF</dt><dd>{flag(data.spf_found)}</dd></div>
                                    <div><dt>SPF Policy</dt><dd>{show(data.spf_policy)}</dd></div>
                                    <div><dt>DMARC</dt><dd>{flag(data.dmarc_found)}</dd></div>
                                    <div><dt>DMARC Policy</dt><dd>{show(data.dmarc_policy)}</dd></div>
                                </dl>
                            </div>
                        </div>

                        <div className="ed-advanced">
                            <h2 className="ed-advanced-title">Advanced Details</h2>
                            {/* Plain read-only values, not input boxes (they looked editable). */}
                            <dl className="ed-advanced-grid">
                                <div>
                                    <dt>Mail Server IP</dt>
                                    <dd>{show(data.mail_server_ip)}</dd>
                                </div>
                                <div>
                                    <dt>MX Record</dt>
                                    <dd>{topMx ? `${topMx.host} (priority ${topMx.priority})` : "—"}</dd>
                                </div>
                                <div>
                                    <dt>Full SPF Record</dt>
                                    <dd className="ed-record">{show(data.spf_record)}</dd>
                                </div>
                                <div>
                                    <dt>Full DMARC Record</dt>
                                    <dd className="ed-record">{show(data.dmarc_record)}</dd>
                                </div>
                                <div>
                                    <dt>Sub Status</dt>
                                    <dd>{show(data.raw_status)}</dd>
                                </div>
                            </dl>
                        </div>
                    </>
                )}
            </section>

        </div>
    );
};

export default EmailDetails;
